"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { m } from "motion/react";
import Image from "next/image";
import { ExternalLink } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { TVOverlay } from "@/components/tv-overlay";

interface ClaimSuccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  earnedUSD: string;
  earnedBGT: string;
  currentAPR?: string;
  txHash?: string;
  onBoost: () => void;
  onSkip: () => void;
}

export function ClaimSuccessDialog({
  open,
  onOpenChange,
  earnedUSD,
  earnedBGT,
  currentAPR,
  txHash,
  onBoost,
  onSkip,
}: ClaimSuccessDialogProps) {
  const blockExplorerUrl = txHash ? `https://bartio.beratrail.io/tx/${txHash}` : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-visible">
          <m.div
            initial={{ scale: 0.8, opacity: 0, rotateX: -15 }}
            animate={{ scale: 1, opacity: 1, rotateX: 0 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
            className="relative w-full overflow-visible"
          >
            {/* Poku peeking from left side */}
            <m.div
              className="absolute -left-20 top-1/2 -translate-y-1/2 z-10"
              initial={{ x: -50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.5, type: "spring", stiffness: 300, damping: 20 }}
            >
              <m.div
                animate={{
                  rotate: [-5, -3, -5],
                  y: [0, -5, 0]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <Image
                  src="/poku/steal.png"
                  alt="Poku peeking"
                  width={120}
                  height={120}
                  className="object-contain"
                  style={{
                    filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.4))",
                  }}
                />
              </m.div>
            </m.div>

            {/* Poku peeking from right side - slightly lower */}
            <m.div
              className="absolute -right-16 top-[60%] -translate-y-1/2 z-10"
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.5, type: "spring", stiffness: 300, damping: 20 }}
            >
              <m.div
                animate={{
                  rotate: [3, 5, 3],
                  y: [0, -3, 0]
                }}
                transition={{
                  duration: 3.5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <Image
                  src="/poku/steal.png"
                  alt="Poku peeking"
                  width={100}
                  height={100}
                  className="object-contain"
                  style={{
                    filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.4))",
                    transform: "scaleX(-1)", // Mirror to face inward
                  }}
                />
              </m.div>
            </m.div>

            {/* TV Frame with Dark CRT aesthetic - adapted from social-feed */}
            <div
              className="relative w-full rounded-xl z-20"
              style={{
                background: `
                  linear-gradient(145deg,
                    #4a4a52 0%,
                    #3a3a42 30%,
                    #2a2a32 70%,
                    #1a1a22 100%
                  )
                `,
                boxShadow: `
                  0 12px 32px rgba(0, 0, 0, 0.6),
                  0 6px 16px rgba(0, 0, 0, 0.4),
                  inset 0 2px 0 rgba(255, 255, 255, 0.15),
                  inset 0 -2px 0 rgba(0, 0, 0, 0.4)
                `,
                border: "2px solid rgba(0, 0, 0, 0.5)",
              }}
            >
              {/* Top Bezel Section - Dark Grey */}
              <div
                className="relative h-6 rounded-t-xl"
                style={{
                  background: `
                    linear-gradient(180deg,
                      #4a4a52 0%,
                      #3a3a42 50%,
                      #2a2a32 100%
                    )
                  `,
                  boxShadow: `
                    inset 0 1px 0 rgba(255, 255, 255, 0.2),
                    inset 0 -1px 0 rgba(0, 0, 0, 0.3)
                  `,
                }}
              />

              {/* Main Bezel Body */}
              <div className="relative flex">
                {/* Left Bezel */}
                <div className="w-4" />

                {/* Screen Area Container - Simplified 2-layer bezel from social-feed */}
                <div className="relative mx-auto w-full flex-none shrink-0">
                  {/* Outer Screen Bezel - Single dark frame */}
                  <div
                    className="rounded-lg p-2"
                    style={{
                      background: `linear-gradient(145deg, #1a1a1c 0%, #0a0a0c 100%)`,
                      boxShadow: `
                        inset 0 2px 4px rgba(0, 0, 0, 0.8),
                        inset 0 -1px 2px rgba(255, 255, 255, 0.03),
                        0 1px 0 rgba(255, 255, 255, 0.02)
                      `,
                    }}
                  >
                    {/* Screen Area - Improved contrast */}
                    <div
                      className="relative overflow-hidden rounded-md"
                      style={{
                        background: `linear-gradient(135deg, #000000 0%, #0a0a0a 100%)`,
                        boxShadow: `
                          inset 0 4px 12px rgba(0, 0, 0, 0.9),
                          inset 0 -1px 2px rgba(255, 255, 255, 0.02)
                        `,
                      }}
                    >
                      {/* TV Overlay Component - includes scanlines, static, vignette */}
                      <TVOverlay intensity="light" />

                      {/* Main Content */}
                      <div
                        className="relative flex flex-col items-center justify-center px-12 py-12"
                        style={{
                          background: `linear-gradient(135deg, #000000 0%, #0f0f10 100%)`,
                          minHeight: "340px",
                        }}
                      >
                        {/* Title */}
                        <m.h1
                          initial={{ scale: 0 }}
                          animate={{ scale: [0, 1.3, 1] }}
                          transition={{ duration: 0.5, delay: 0.2 }}
                          className="mb-3 font-pressStart2P text-xl text-white"
                          style={{
                            textShadow: `
                              2px 2px 0 #000,
                              4px 4px 8px rgba(0,0,0,0.8)
                            `,
                          }}
                        >
                          SUCCESSFULLY CLAIMED
                        </m.h1>

                        {/* Transaction Link */}
                        {blockExplorerUrl && (
                          <m.a
                            href={blockExplorerUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.3 }}
                            className="mb-8 inline-flex items-center gap-1 font-pressStart2P text-[9px] text-muted-foreground transition-[color,transform] hover:text-muted-foreground/80 hover:scale-105"
                          >
                            VIEW TRANSACTION
                            <ExternalLink className="h-3 w-3" />
                          </m.a>
                        )}

                        {/* Earnings Display - Stacked */}
                        <m.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ delay: 0.4 }}
                          className="mb-10 flex flex-col items-center gap-4"
                        >
                          {/* USD Value - Primary Focus */}
                          <div
                            className="font-pressStart2P text-3xl text-white"
                            style={{
                              textShadow: `
                                0 4px 8px rgba(0,0,0,0.8),
                                2px 2px 0 rgba(0,0,0,0.6)
                              `,
                            }}
                          >
                            ${earnedUSD}
                          </div>

                          {/* BGT Amount with Icon */}
                          <div className="flex items-center gap-3">
                            <Image
                              src="/token/bgt.png"
                              alt="BGT"
                              width={32}
                              height={32}
                              className="rounded-full"
                              style={{
                                boxShadow: "0 0 15px rgba(245,209,85,0.25)",
                              }}
                            />
                            <div
                              className="font-pressStart2P text-xl text-muted-foreground"
                              style={{
                                textShadow: `
                                  0 2px 4px rgba(0,0,0,0.6)
                                `,
                              }}
                            >
                              {earnedBGT}
                            </div>
                          </div>
                        </m.div>

                        {/* Action Buttons */}
                        <div className="flex gap-4">
                          <m.button
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={onSkip}
                            className="rounded px-6 py-3 font-pressStart2P text-[10px] text-gray-300"
                            style={{
                              background: "linear-gradient(145deg, #3a3a42 0%, #2a2a32 100%)",
                              border: "3px solid rgba(0,0,0,0.5)",
                              boxShadow: `
                                0 3px 0 rgba(0,0,0,0.4),
                                0 2px 4px rgba(0,0,0,0.5),
                                inset 0 1px 1px rgba(255,255,255,0.1),
                                inset 0 -1px 1px rgba(0,0,0,0.5)
                              `,
                            }}
                          >
                            SKIP
                          </m.button>

                          <m.button
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={onBoost}
                            className="rounded px-6 py-3 font-pressStart2P text-[10px] text-black"
                            style={{
                              background: "linear-gradient(145deg, #F5D155 0%, #C4A62F 100%)",
                              border: "3px solid #000000",
                              boxShadow: `
                                0 4px 0 rgba(0,0,0,0.4),
                                0 2px 6px rgba(0,0,0,0.5),
                                inset 0 1px 0 rgba(255,255,255,0.4),
                                inset 0 -1px 0 rgba(0,0,0,0.3),
                                0 0 30px rgba(245,209,85,0.3)
                              `,
                            }}
                          >
                            EARN MORE BY BOOSTING
                          </m.button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Bezel */}
                <div className="w-4" />
              </div>

              {/* Bottom Bezel - Dark Grey aesthetic */}
              <div
                className="relative h-10 rounded-b-xl"
                style={{
                  background: `
                    linear-gradient(180deg,
                      #3a3a42 0%,
                      #2a2a32 30%,
                      #1a1a22 70%,
                      #0a0a12 100%
                    )
                  `,
                  boxShadow: `
                    0 2px 4px rgba(0, 0, 0, 0.4),
                    inset 0 2px 0 rgba(255, 255, 255, 0.15),
                    inset 0 -2px 0 rgba(0, 0, 0, 0.2),
                    0 1px 0 rgba(255, 255, 255, 0.05)
                  `,
                }}
              >
                {/* TV Label */}
                <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center h-full">
                  <span
                    className="font-pressStart2P text-[7px] tracking-wider"
                    style={{
                      color: "#6a6a72",
                      textShadow: `
                        0 1px 0 rgba(255, 255, 255, 0.2),
                        0 -1px 0 rgba(0, 0, 0, 0.6)
                      `,
                      lineHeight: "1",
                    }}
                  >
                    HENLO-TV
                  </span>
                </div>

                {/* Status LEDs */}
                <div className="absolute right-4 top-1/2 flex -translate-y-1/2 items-center gap-2">
                  {/* Glass Bulb Status LEDs with 3D Effect (from social-feed) */}
                  {/* Yellow/Loading LED */}
                  <div className="relative size-2 shrink-0">
                    {/* Glass bulb outer shell */}
                    <div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: "linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0) 100%)",
                        boxShadow: `
                          inset 0 1px 1px rgba(255, 255, 255, 0.7),
                          inset 0 -0.5px 1px rgba(0, 0, 0, 0.3),
                          0 1px 3px rgba(0, 0, 0, 0.4)
                        `,
                      }}
                    />
                    {/* Inner light source */}
                    <div
                      className="absolute inset-[1px] rounded-full"
                      style={{
                        background: "radial-gradient(circle at center, rgba(243, 199, 55, 0.3) 0%, rgba(243, 199, 55, 0.2) 40%, transparent 100%)",
                        mixBlendMode: "screen",
                      }}
                    />
                    {/* Glass shine */}
                    <div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: "linear-gradient(to bottom, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.2) 30%, transparent 50%)",
                      }}
                    />
                  </div>

                  {/* Blue/Data LED */}
                  <div className="relative size-2 shrink-0">
                    {/* Glass bulb outer shell */}
                    <div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: "linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0) 100%)",
                        boxShadow: `
                          inset 0 1px 1px rgba(255, 255, 255, 0.6),
                          inset 0 -0.5px 1px rgba(0, 0, 0, 0.2),
                          0 1px 2px rgba(0, 0, 0, 0.3)
                        `,
                        filter: "drop-shadow(0 0 3px rgba(52, 107, 255, 0.4)) drop-shadow(0 0 6px rgba(52, 107, 255, 0.2))",
                      }}
                    />
                    {/* Inner light source */}
                    <div
                      className="absolute inset-[1px] rounded-full"
                      style={{
                        background: "radial-gradient(circle at center, rgba(52, 107, 255, 1) 0%, rgba(52, 107, 255, 0.7) 40%, transparent 100%)",
                        mixBlendMode: "screen",
                      }}
                    />
                    {/* Glass shine */}
                    <div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: "linear-gradient(to bottom, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.2) 30%, transparent 50%)",
                      }}
                    />
                  </div>

                  {/* Green/Power LED */}
                  <div className="relative size-2 shrink-0">
                    {/* Glass bulb outer shell */}
                    <div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: "linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0) 100%)",
                        boxShadow: `
                          inset 0 1px 1px rgba(255, 255, 255, 0.7),
                          inset 0 -0.5px 1px rgba(0, 0, 0, 0.3),
                          0 1px 3px rgba(0, 0, 0, 0.4)
                        `,
                      }}
                    />
                    {/* Inner light source */}
                    <div
                      className="absolute inset-[1px] rounded-full"
                      style={{
                        background: "radial-gradient(circle at center, rgba(187, 247, 208, 1) 0%, rgba(16, 185, 129, 0.8) 35%, transparent 100%)",
                        mixBlendMode: "screen",
                      }}
                    />
                    {/* Glass shine */}
                    <div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: "linear-gradient(to bottom, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.2) 30%, transparent 50%)",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </m.div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </Dialog>
  );
}

export default ClaimSuccessDialog;